import { usePrevious, useQueryState } from 'commons/utils';
import { SearchCategories } from 'components/mediaLibrary/constants';
import { PlaceHolderSegment } from 'components/PlaceholderSegment';
import { Spinner } from 'components/spinner';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { IState } from 'reducers/reducers';
import { languageState } from 'redux-recoil-sync.state';
import { Dimmer, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { getSearchModePreference, getView, setSearchModePreference, toggleView, View } from '../blockUtils';
import BlockList from './BlockList';
import { GoBackToLocaitonState } from './Blocks.types';
import { BlockCards } from './components/BlockCards';
import { BlocksHeader } from './components/BlocksHeader';
import { BlockOverviewSearch } from './components/BlockOverviewSearch';
import { BlocksTable } from './components/BlocksTable';
import { ClassicBlockSearch } from './components/ClassicBlockSearch';
import { BlockResultsBar } from './components/BlockResultsBar';
import { SearchModeSlider } from './components/SearchModeSlider';
import {
  clampBlockSearchPage,
  getNewSearchParam,
  getShownSearchModePreference,
  isNewSearchDefault,
  migrateClassicSearch,
  NEW_SEARCH_PARAM,
  preferNewSearch,
  SearchModePreference,
} from './blockSearch';
import useBlockParams from './UseBlockParams';
import useLegacyBlockParams from './UseLegacyBlockParams';

interface OwnProps {
  useAsPicker?: boolean;
  onClick?: (id: string) => void;
}

/**
 * URL params used by either search mode; they are cleared when switching, as the two query syntaxes differ. Switching to
 * the new search carries the classic search over as a query.
 */
const SEARCH_URL_PARAMS = ['query', 'page', 'category', 'showArchived'];

/**
 * The search term to apply: debounced while typing, but an emptied term applies at once and resets the debounce, so
 * neither search runs a term that was cleared when typing starts again. A term set by switching the search (a new
 * `searchKey`) also applies at once, so the other search never runs the term it replaced.
 */
function useAppliedSearchTerm(searchTerm: string, debounceMs: number, searchKey: boolean): string {
  const [debounced, setDebounced] = useState({ searchTerm, searchKey });
  const applyAtOnce = searchTerm === '' || debounceMs <= 0 || searchKey !== debounced.searchKey;

  useEffect(() => {
    if (applyAtOnce) {
      setDebounced({ searchTerm, searchKey });
      return undefined;
    }

    const timeoutHandle = window.setTimeout(() => setDebounced({ searchTerm, searchKey }), debounceMs);
    return () => window.clearTimeout(timeoutHandle);
  }, [searchTerm, debounceMs, searchKey, applyAtOnce]);

  return applyAtOnce ? searchTerm : debounced.searchTerm;
}

export function BlockOverview({ useAsPicker, onClick }: OwnProps) {
  const history = useHistory<GoBackToLocaitonState>();
  const isOverviewPage = !!useRouteMatch('/block/overview')?.isExact;
  const isInBackground = !!history.location.state?.goBackTo;
  const [preference, setPreference] = useState(getSearchModePreference);
  const newSearchDefault = isNewSearchDefault();

  // The mode comes from the overview's own URL: the current one, or behind the block editor the one it returns to. The
  // block picker has no URL of its own (it lives on the composition page), so it uses the remembered pick.
  const modeSearch = isInBackground ? (history.location.state?.goBackTo?.search ?? '') : history.location.search;
  const newSearchParam = useAsPicker ? null : getNewSearchParam(modeSearch);
  // The URL decides; without it, the user's last pick on the slider, or the environment's default
  const isNewSearch = newSearchParam ?? preferNewSearch(preference, newSearchDefault);
  // Only the overview page itself keeps the mode in its URL; not the block editor's URL or the composition page's
  const ownsModeParam = !useAsPicker && !isInBackground && isOverviewPage;

  // Always keep the mode in the URL, so a shared or bookmarked link opens the same search
  useEffect(() => {
    if (ownsModeParam && newSearchParam === null) {
      const params = new URLSearchParams(history.location.search);
      params.set(NEW_SEARCH_PARAM, String(isNewSearch));
      history.replace({ ...history.location, search: `?${params}` });
    }
  }, [ownsModeParam, newSearchParam, isNewSearch, history]);

  // Both searches live in this one component, so switching keeps the header and results mounted instead of collapsing
  // (and, in the block picker, re-centring) the whole overview while the other search loads.
  const [searchTerm, setSearchTerm] = useQueryState<string>('query', ''); // archived blocks are hidden server-side unless the query uses archived:
  const [rawPage, setPage] = useQueryState<number>('page', 1); // start on page 1
  const page = clampBlockSearchPage(rawPage);
  const [searchCategory, setSearchCategory] = useQueryState<SearchCategories>('category', SearchCategories.All); // classic only
  const [showArchived, setShowArchived] = useQueryState<boolean>('showArchived', false); // classic only

  // Only a pick on the slider is remembered, not a search opened by default or from a link
  const handleSearchModeChange = (newPreference: SearchModePreference) => {
    setPreference(newPreference);
    setSearchModePreference(newPreference);

    const newSearch = preferNewSearch(newPreference, newSearchDefault);
    if (newSearch === isNewSearch) {
      return;
    }

    // Clear this mode's search params in the same URL change, as the two query syntaxes differ; a classic search is
    // carried over to the new search as a query
    const params = new URLSearchParams(history.location.search);
    SEARCH_URL_PARAMS.forEach((param) => params.delete(param));
    const migratedQuery = newSearch ? migrateClassicSearch(searchTerm, searchCategory, showArchived) : '';
    if (migratedQuery) {
      params.set('query', migratedQuery);
    }
    if (ownsModeParam) {
      params.set(NEW_SEARCH_PARAM, String(newSearch));
    }
    const search = params.toString();
    history.replace({ ...history.location, search: search ? `?${search}` : '' });
  };

  const wasInBackground = usePrevious(isInBackground, true);
  const appliedSearchTerm = useAppliedSearchTerm(searchTerm, wasInBackground ? 0 : 750, isNewSearch);

  // Only the debounced term is parsed, so the error does not flicker while typing; only the shown search fetches
  const { query: newSearchQuery, searchError } = useBlockParams(page, appliedSearchTerm, isInBackground || !isNewSearch);
  const classicQuery = useLegacyBlockParams(page, searchCategory, appliedSearchTerm, showArchived, isInBackground || isNewSearch);

  const handleSearchChange = (term: string) => {
    setPage(1);
    setSearchTerm(term);
  };

  return (
    <BlockOverviewLayout
      activePage={page}
      blocksQuery={isNewSearch ? newSearchQuery : classicQuery}
      onClick={onClick}
      onPageChange={setPage}
      searchModeToggle={
        <SearchModeSlider
          value={getShownSearchModePreference(preference, newSearchDefault, isNewSearch)}
          newSearchDefault={newSearchDefault}
          onChange={handleSearchModeChange}
        />
      }
      useAsPicker={useAsPicker}
    >
      {isNewSearch ? (
        <BlockOverviewSearch onSearchChange={handleSearchChange} searchError={searchError} searchTerm={searchTerm} />
      ) : (
        <ClassicBlockSearch
          onCategoryChange={(category) => {
            setSearchCategory(category);
            setPage(1);
          }}
          onSearchChange={handleSearchChange}
          onShowArchivedChange={setShowArchived}
          searchCategory={searchCategory}
          searchTerm={searchTerm}
          showArchived={showArchived}
        />
      )}
    </BlockOverviewLayout>
  );
}

interface LayoutProps extends OwnProps {
  activePage: number;
  blocksQuery: UseQueryResult<BlockList>;
  onPageChange: (page: number) => void;
  searchModeToggle: ReactNode;
  /** The search area */
  children: ReactNode;
}

function BlockOverviewLayout({ useAsPicker, onClick, activePage, blocksQuery, onPageChange, searchModeToggle, children }: LayoutProps) {
  const [language] = useRecoilState(languageState);
  const history = useHistory<GoBackToLocaitonState>();
  const [showList, setShowList] = useState(getView() === View.LIST);

  // While the first results of a changed search load (e.g. after switching the search), keep showing the last results,
  // so the list does not collapse and reflow; they are dimmed until the new ones arrive.
  // Only the search's own results count as "last shown": a query's placeholder data from an older key (react-query's
  // keepPreviousData) may belong to a search made before switching away and back.
  const lastBlocks = useRef<BlockList | undefined>(undefined);
  if (blocksQuery.data && !blocksQuery.isPreviousData) {
    lastBlocks.current = blocksQuery.data;
  }
  const blocks = blocksQuery.isFetching ? (lastBlocks.current ?? blocksQuery.data) : blocksQuery.data;
  const blocksErrorMessage = blocksQuery.error instanceof Error ? blocksQuery.error.message : 'Unknown error';

  const handleOnClickBlock = (id: string) => {
    if (onClick) {
      onClick(id);
    } else {
      const { pathname, search, hash } = window.location;
      history.push({
        pathname: `/block/overview/${id}`,
        state: {
          goBackTo: {
            pathname,
            search,
            hash,
          },
        },
      });
    }
  };

  const blockList = (): JSX.Element | null => {
    if (!blocks) {
      return null;
    }
    return showList ? (
      <BlocksTable items={blocks.entries} language={language} onClick={handleOnClickBlock} />
    ) : (
      <BlockCards items={blocks.entries} language={language} onClick={handleOnClickBlock} />
    );
  };

  return (
    <div>
      <UploadingOverlay />
      <div>
        <BlocksHeader
          showList={showList}
          useAsPicker={useAsPicker}
          searchModeToggle={searchModeToggle}
          onCreateBlock={() => history.push(`/block/overview/new`)}
          onToogleList={() => {
            setShowList(!showList);
            toggleView();
          }}
        >
          {children}
        </BlocksHeader>
        <BlockResultsBar
          activePage={activePage}
          count={!blocksQuery.isError && blocks ? { total: blocks.count, shown: blocks.entries.length } : undefined}
          loading={blocksQuery.isFetching}
          onPageChange={onPageChange}
          totalPages={blocks?.pages ?? 0}
        />

        {blocksQuery.isFetching ? <Spinner visible={blocksQuery.isFetching} /> : null}
        {blocksQuery.isError ? (
          <PlaceHolderSegment iconName="exclamation triangle" header="An Error happend while loading blocks :/">
            <pre>{blocksErrorMessage}</pre>
          </PlaceHolderSegment>
        ) : (
          // The previous results stay in place (dimmed) while new ones load, so the page and the block picker keep their height
          <Results $loading={blocksQuery.isFetching}>{blockList()}</Results>
        )}
      </div>
    </div>
  );
}

const Results = styled.div<{ $loading: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0.45 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};
  transition: opacity 0.15s ease;
`;

function UploadingOverlay() {
  const isUploading: boolean = useSelector((state: IState) => state.blocks.isUploading);

  if (!isUploading) return null;
  return (
    <Dimmer active inverted>
      <Loader size="small">Doing some hard work. Please wait</Loader>
    </Dimmer>
  );
}
